import { FormlyFieldConfig } from '@ngx-formly/core';
import { isDisplayOnlyFieldType } from '../display-only-field-types';

/** Handoff #20830: locked fields explain themselves instead of silently ignoring input. */
export const LOCKED_FIELD_TITLE = 'Dit veld kan niet worden bewerkt';

/**
 * Supplies the generic hover-tooltip wrapper with a locked explanation.
 * Explicit hover help remains authoritative and readonly fields stay interactive.
 *
 * A locked field that carries its own explanation (`props.description`) shows that instead of the
 * generic text. The explanation normally renders as the field's hint, but the disabled state hides
 * every hint (`.mat-form-field-disabled mat-hint` in styles/tailwind.scss), so on a locked field the
 * hover is the only place it can still be read — and for a field the system fills, it says why the
 * field is locked and where its value comes from, which the generic text cannot.
 *
 * Display-only field types (`isDisplayOnlyFieldType`, form builder group "Opmaak formulier")
 * never take input, so a "cannot be edited" explanation would mislead rather than help. This used
 * to be a hand-copied list here and quietly missed `readonly-input` and `interface-action-button`
 * — deriving it keeps the two catalogues from drifting apart again.
 */
export function lockedFieldExtension(field: FormlyFieldConfig): void {
  if (!field.props || field.fieldGroup || field.key === undefined || field.key === null) {
    return;
  }

  if (isDisplayOnlyFieldType(typeof field.type === 'string' ? field.type : undefined)) {
    return;
  }

  const tooltipExpression = 'props.hoverTooltip';
  const nativeTitle = field.props.attributes?.['title'];
  if (field.props.hoverTooltip !== undefined || field.expressions?.[tooltipExpression] || nativeTitle !== undefined) {
    return;
  }

  field.expressions = {
    ...field.expressions,
    [tooltipExpression]: () => {
      // The page read view (#21239) stamps `readonly` AND `disabled` on own-view fields so their
      // buttons go inert. That is reading, not a lock — a "cannot be edited" hint on every field
      // would describe the whole page. Both spellings: module config uses camelCase `readOnly`.
      if (field.props?.readonly || field.props?.['readOnly']) {
        return undefined;
      }
      return field.props?.disabled || field.formControl?.disabled ? lockedExplanation(field) : undefined;
    },
  };
}

/** The field's own explanation as plain text, or the generic locked text when it has none. */
function lockedExplanation(field: FormlyFieldConfig): string {
  const description: unknown = field.props?.description;
  if (typeof description !== 'string') {
    return LOCKED_FIELD_TITLE;
  }
  // The tooltip escapes its text, so markup in a description would show as literal tags.
  const text = description.includes('<')
    ? (new DOMParser().parseFromString(description, 'text/html').body.textContent ?? '')
    : description;
  return text.trim() || LOCKED_FIELD_TITLE;
}
